/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 폼 패널 클래스를 정의한다.
 *
 * @file /src/components/form/Form.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import * as parent from '@components/panel/Panel';
import { Field } from '@components/form/field/Field';
import { Message } from '@components/message/Message';
import { Ajax } from '@utils/Ajax';
import { Language, LanguageText } from '@utils/Language';
import { Exceptions } from '@utils/Exceptions';
import './Form.css';

export interface Request {
    url: string;
    params?: { [key: string]: string | number };
    message?: LanguageText;
}

export interface Response {
    success: boolean;
    message?: string;
    data?: { [key: string]: any };
    [key: string]: any;
}

export interface FieldDefaults {
    labelAlign?: 'left' | 'right' | 'center';
    labelWidth?: number;
    labelLength?: number;
    labelSeparator?: string;
    labelPosition?: 'left' | 'top';
}

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드 기본값
     */
    fieldDefaults?: FieldDefaults;

    /**
     * 필드값이 NULL 인 필드데이터도 SUBMIT 할지 여부
     */
    isSubmitNull?: boolean;

    /**
     * 폼 로딩이 될때까지 렌더링 중으로 판단할지 여부
     */
    isRenderingUntilLoaded?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Form extends parent.Panel {
    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'form';

    /**
     * 필드기본값
     */
    fieldDefaults: FieldDefaults | null;

    /**
     * 필드값이 NULL 인 필드데이터도 SUBMIT 할지 여부
     */
    isSubmitNull: boolean;

    /**
     * 폼 로딩이 될때까지 렌더링 중으로 판단할지 여부
     */
    isRenderingUntilLoaded: boolean;

    /**
     * 폼 패널을 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Form> = null) {
        super(properties);

        this.fieldDefaults = this.properties.fieldDefaults ?? null;
        this.isSubmitNull = this.properties.isSubmitNull === true;
        this.isRenderingUntilLoaded = this.properties.isRenderingUntilLoaded === true;
        this.$scrollable = this.$getContent();
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        super.initItems();
    }

    /**
     * 폼 패널에 속한 필드를 가져온다.
     *
     * @param name - 필드명
     * @returns 필드
     */
    getField<T extends Field = Field>(name: string): T | null {
        for (const field of this.getFields()) {
            if (field.inputName === name) {
                return field as T;
            }
        }

        return null;
    }

    /**
     * 폼 패널에 속한 모든 필드를 가져온다.
     *
     * @returns 전체필드
     */
    getFields(): Field[] {
        const fields: Field[] = [];
        for (const item of this.items ?? []) {
            if (item instanceof Field) {
                fields.push(...item.getFields());
            }
        }

        return fields;
    }

    /**
     * 폼 패널에 속한 모든 필드의 값을 가져온다.
     *
     * @returns 필드값
     */
    async getValues(): Promise<{ [key: string]: any }> {
        /**
         * 폼 패널이 렌더링 되지 않았다면, 강제로 렌더링한다.
         */
        if (this.isRendered() == false) {
            await this.render(true);
        }

        const values: { [key: string]: any } = {};

        for (const field of this.getFields()) {
            if (field instanceof Field) {
                const data = await field.getValues();
                Object.assign(values, data);
            }
        }

        return values;
    }

    /**
     * 폼 패널에 속한 모든 필드의 값을 지정한다.
     *
     * @param data - 필드값
     * @param is_init - 초기값여부
     */
    async setValues(data: { [key: string]: any }, is_init: boolean = false): Promise<void> {
        /**
         * 폼 패널이 렌더링 되지 않았다면, 강제로 렌더링한다.
         */
        if (this.isRendered() == false) {
            await this.render(true);
        }

        const promises: Promise<void>[] = [];

        for (const name in data) {
            promises.push(this.getField(name)?.setValue(data[name], is_init));
        }

        await Promise.all(promises);
    }

    /**
     * 특정필드로 스크롤 한다.
     */
    scrollTo(field: Field): void {
        field?.$getComponent().scrollTo(true, null, { top: 10, bottom: 10 });
    }

    /**
     * 에러가 발생한 첫번째 필드로 스크롤 한다.
     */
    scrollToError(): void {
        for (const field of this.getFields()) {
            if (field.isValidated() === false) {
                this.scrollTo(field);
                break;
            }
        }
    }

    /**
     * 폼 패널에 속한 모든 필드가 유효한지 확인한다.
     *
     * @returns 유효여부
     */
    async isValid(): Promise<boolean> {
        /**
         * 폼 패널이 렌더링 되지 않았다면, 강제로 렌더링한다.
         */
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.isLoading() === true) {
            Message.info({ message: Language.getError('WAITING_FOR_ACTION') });
            return false;
        }

        const validations: Promise<boolean>[] = [];

        this.getFields().forEach((field) => {
            if (this.isDisabled() === false) {
                validations.push(field.isValid());
            }
        });

        const validates = await Promise.all(validations);
        for (const validate of validates) {
            if (validate == false) {
                return false;
            }
        }

        return true;
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     */
    focus(): void {
        const fields = this.getFields();
        for (const field of fields) {
            if (field.isShow() == true && field.isDisabled() == false) {
                field.focus();
                return;
            }
        }

        super.focus();
    }

    /**
     * 폼패널이 로딩상태이거나 필드값이 지정되고 있는지 확인한다.
     *
     * @returns 로딩여부
     */
    isLoading(): boolean {
        if (this.loading?.isShow() === true) {
            return true;
        } else {
            for (const field of this.getFields()) {
                if (field.isLoading() == true) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * 폼 패널 데이터를 불러온다.
     *
     * @params loader - 요청정보
     * @returns 응답데이터
     */
    async load(loader: Request): Promise<Response> {
        const { url, params = null, message = null } = loader;

        if (this.isRendered() == false) {
            await this.render(true);
        }

        this.setLoading('column', message ?? Language.getAction('LOADING'));

        try {
            const response = (await Ajax.get(url, params)) as Response;
            if (response.success == true) {
                await this.setValues(response.data ?? {}, true);
            }

            this.setLoading(false);
            this.fireEvent('load', [this, response]);

            return response;
        } catch (e) {
            const error = Exceptions.base(e);

            return new Promise((resolve) => {
                Exceptions.print(error, async () => {
                    this.setLoading(false);
                    this.fireEvent('load', [this, error.getResponse()]);

                    resolve(error.getResponse());
                });
            });
        }
    }

    /**
     * 폼 패널을 전송한다.
     *
     * @param sender - 요청정보
     * @returns 응답데이터
     */
    async submit(sender: Request): Promise<Response> {
        const { url, params = null, message = null } = sender;

        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.isLoading() === true) {
            Message.info({ message: Language.getError('WAITING_FOR_ACTION') });
            return { success: false };
        }

        const isValid = await this.isValid();
        if (isValid === false) {
            this.scrollToError();
            return { success: false };
        }

        this.setLoading('column', message ?? Language.getAction('SENDING'));

        let data: { [key: string]: any };
        try {
            data = await this.getValues();
        } catch (e) {
            const error = Exceptions.base(e);

            return new Promise((resolve) => {
                Exceptions.print(error, async () => {
                    this.setLoading(false);
                    this.fireEvent('submit', [this, { success: false }]);
                    resolve({ success: false });
                });
            });
        }

        if (this.isSubmitNull === false) {
            for (const name in data) {
                if (data[name] === null) {
                    delete data[name];
                }
            }
        }

        try {
            const response = await Ajax.post(url, data, params, false);
            this.setLoading(false);
            this.fireEvent('submit', [this, response]);

            return response;
        } catch (e) {
            const error = Exceptions.base(e);
            const errors = error.getErrors();

            if (errors !== null) {
                for (const name in errors) {
                    this.getField(name)?.updateValidation({ valid: false, message: errors[name] });
                }
            }

            return new Promise((resolve) => {
                Exceptions.print(error.setCode('FAILED_TO_SAVE_DATA'), async () => {
                    this.setLoading(false);
                    this.fireEvent('submit', [this, error.getResponse()]);

                    if (errors !== null) {
                        this.scrollToError();
                    }

                    resolve(error.getResponse());
                });
            });
        }
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();

        if (this.fieldDefaults !== null) {
            const labelWidth = this.fieldDefaults.labelWidth ?? null;
            const labelLength = this.fieldDefaults.labelLength ?? null;
            if (labelWidth !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelWidth}px`);
            } else if (labelLength !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelLength}rem`);
            }

            const labelPosition = this.fieldDefaults.labelPosition ?? 'left';
            if (labelPosition != 'left') {
                this.$getComponent().setStyleProperty('--aui-form-label-width', '100%');
                this.$getComponent().setStyleProperty('--aui-form-columns', '1fr auto');
                this.$getComponent().setStyleProperty(
                    '--aui-form-areas',
                    `'label label' 'content buttons' 'helptext helptext'`
                );
            }

            const labelAlign = this.fieldDefaults.labelAlign ?? (labelPosition == 'top' ? 'left' : 'right');
            if (labelAlign != 'right') {
                this.$getComponent().setStyleProperty('--aui-form-label-align', labelAlign);
                this.$getComponent().setStyleProperty('--aui-form-label-padding', '0');
            }

            const labelSeparator = this.fieldDefaults.labelSeparator ?? (labelPosition == 'top' ? '' : ':');
            if (labelSeparator !== ':') {
                this.$getComponent().setStyleProperty('--aui-form-label-separator', `"${labelSeparator}"`);
            }
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();

        if (this.isRenderingUntilLoaded == true) {
            this.setLoading('column', Language.getAction('LOADING'));
        }
    }
}
